/**
 * SoleadLab 2.0 — FL-3a create-then-attach flow glue
 *
 * Field techs report photo-first ("Use photo location") or create-then-shoot.
 * Either way a just-created mark may have a photo waiting for it
 * (uiStore.pendingFieldPhoto). MapCanvas calls handleFieldQuickAddCreated()
 * after every armed quick-add create; it is a strict no-op for operators, so
 * the rapid-fire operator flow is untouched.
 *
 * Field-mode behavior per create:
 *  1. Open the new mark's Field update card (photo/notes one tap away —
 *     owner flow requirement 2026-08-13). The armed state persists, so
 *     closing the card resumes placement.
 *  2. If a photo is pending: wait for the create's server confirmation
 *     (the attach endpoint 404s on unknown ids), downscale, attach, toast.
 *     Failure leaves the mark in place — the tech re-taps Add picture.
 */

import { useUIStore } from '../stores/uiStore';
import { useMapStore } from '../stores/mapStore';
import { useProjectStore, waitForIndicationConfirmed } from '../stores/projectStore';
import { useShareSessionStore } from '../stores/shareSessionStore';
import { useAuthStore } from '../stores/authStore';
import { isFieldLinkState } from '../hooks/useShareViewer';
import { downscaleImageToJpeg } from './downscaleImage';

function isFieldMode(): boolean {
  const share = useShareSessionStore.getState();
  return isFieldLinkState(share.active, useAuthStore.getState().token, share.view);
}

export function handleFieldQuickAddCreated(indicationId: string): void {
  if (!isFieldMode()) return;
  // C1: the card opens immediately for the fresh mark.
  useMapStore.getState().setPreviewIndication(indicationId);
  // C2: a waiting photo attaches automatically.
  void attachPendingFieldPhoto(indicationId);
}

async function attachPendingFieldPhoto(indicationId: string): Promise<void> {
  const ui = useUIStore.getState();
  const pending = ui.pendingFieldPhoto;
  if (!pending) return;
  ui.setPendingFieldPhoto(null);

  const notify = useUIStore.getState().addNotification;
  ui.setFieldPhotoUploading(indicationId);
  try {
    // 35s: MUST outlast the create POST's own 30s timeout (apiFetch
    // DEFAULT_TIMEOUT) — a shorter wait declared "did not save" for creates
    // that landed late over cell and dropped the photo (review finding).
    // After 35s a null here means the create genuinely failed/rolled back.
    const confirmed = await waitForIndicationConfirmed(indicationId, 35_000);
    if (!confirmed) {
      notify({
        type: 'error',
        title: "Couldn't attach the photo",
        message: 'The new mark did not save. Place it again, then add the picture from its card.',
      });
      return;
    }
    const { blob, name } = await downscaleImageToJpeg(pending.file);
    const result = await useProjectStore
      .getState()
      .addIndicationFieldPhoto(indicationId, blob, name);
    if (result === 'updated') {
      notify({ type: 'success', title: 'Photo added' });
    } else if (result !== 'noop') {
      notify({
        type: 'error',
        title: "Couldn't attach the photo",
        message: 'The mark was saved. Tap it and use Add picture to retry.',
      });
    }
  } finally {
    useUIStore.getState().setFieldPhotoUploading(null);
  }
}
